import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from "@mui/material";
import {
  CustomIntervalType as CustomIntervalTypeEnum,
  RatelistType as RatelistTypeEnum,
} from "@sinch/core/entities/serverEnums";
import { useFormMutationSubmit } from "@sinch/hooks/useFormMutationSubmit";
import { useMuiForm } from "@sinch/hooks/useMuiForm";
import { CustomIntervalType, RatelistType } from "@sinch/types/sinch.types";
import { useSnackbar } from "notistack";
import { propEq } from "ramda";
import { isNotNil, isNotNilOrEmpty } from "ramda-adjunct";
import React, { FC, Suspense, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { InferType } from "yup";
import type {
  CreateIntervalMutation,
  CreateIntervalMutationVariables,
  EditIntervalMutation,
  EditIntervalMutationVariables,
} from "./queries.types";
import { CreateIntervalArchivedField } from "@sinch/components/application/RateInterval/CreateInterval/CreateIntervalArchivedField";
import { CreateIntervalDescriptionField } from "@sinch/components/application/RateInterval/CreateInterval/CreateIntervalDescriptionField";
import { CreateIntervalNameField } from "@sinch/components/application/RateInterval/CreateInterval/CreateIntervalNameField";
import { CreateIntervalTypeField } from "@sinch/components/application/RateInterval/CreateInterval/CreateIntervalTypeField";
import { BreakPenaltySettings } from "@sinch/components/application/RateInterval/CreateInterval/BreakPenaltySettings";
import { DailyOvertimeSettings } from "@sinch/components/application/RateInterval/CreateInterval/DailyOvertimeSettings";
import {
  type CreateIntervalFormData,
  intervalFormSchema,
} from "@sinch/components/application/RateInterval/CreateInterval/formSchema";
import { IntersectionSettings } from "@sinch/components/application/RateInterval/CreateInterval/IntersectionSettings";
import { NationalHolidaysSettings } from "@sinch/components/application/NationalHolidaysSettings/NationalHolidaysSettings";
import { WeeklyOvertimeSettings } from "@sinch/components/application/RateInterval/CreateInterval/WeeklyOvertimeSettings";
import { WorkedDurationSettings } from "@sinch/components/application/RateInterval/CreateInterval/WorkedDurationSettings";
import { useRateInterval } from "@sinch/components/application/RateInterval/UseRateInterval";
import { weekDaySchema } from "@sinch/components/application/TimeIntervalSettings/formSchema";
import {
  TimeIntervalSettings,
  TimeIntervalTypeFormFields,
} from "@sinch/components/application/TimeIntervalSettings/TimeIntervalSettings";
import { CollapsibleListCard } from "@sinch/components/CollapsibleListCard/CollapsibleListCard";
import { FormContainer } from "@sinch/components/form/FormContainer/FormContainer";
import { LoadingWrapper } from "@sinch/components/LoadingWrapper/LoadingWrapper";
import { RatelistOption } from "@sinch/components/application/Rate/components/RatelistSelectInput/RatelistSelectInput";
import { match } from "ts-pattern";
import { useWatch } from "react-hook-form";

interface IntervalDialogProps<TIntervalId extends string | undefined = string> {
  open: boolean;
  onClose: () => void;
  onSave: TIntervalId extends string ? (id?: string) => void : () => void;
  intervalId?: TIntervalId;
  ratelistType?: RatelistType | RatelistTypeEnum;
}

export const IntervalFormDialog: FC<IntervalDialogProps> = ({ open, onClose, onSave, intervalId, ratelistType }) => (
  <Dialog fullWidth maxWidth="sm" onClose={onClose} open={open}>
    <Suspense fallback={<LoadingWrapper on />}>
      <IntervalForm intervalId={intervalId} onClose={onClose} onSave={onSave} ratelistType={ratelistType} />
    </Suspense>
  </Dialog>
);

const IntervalForm: FC<Omit<IntervalDialogProps, "open">> = ({ intervalId, onSave, onClose, ratelistType }) => {
  const { t } = useTranslation(["common", "draft"]);
  const { enqueueSnackbar } = useSnackbar();

  const {
    create,
    edit,
    rateInterval: data,
    loading,
  } = useRateInterval({ id: intervalId, queryOptions: { fetchPolicy: "network-only" } });

  const isTimeType = (
    type: NonNullable<CustomIntervalType | CustomIntervalTypeEnum | undefined>,
    settingsData: any
  ): settingsData is TimeIntervalTypeFormFields => isNotNil(settingsData) && type === CustomIntervalTypeEnum.Time;
  const normalizeTimeDays = (settingsData: any) => ({
    daysOfWeek: Array.from<any[], InferType<typeof weekDaySchema>>({ length: 7 }, (_, i) => ({
      ...weekDaySchema.getDefault(),
      weekDay: i + 1,
      ...settingsData?.daysOfWeek?.find(propEq("weekDay", i + 1)),
    })),
  });
  const transformByType = (
    type: NonNullable<CustomIntervalType | CustomIntervalTypeEnum | undefined>,
    settingsData: any
  ) => {
    if (isTimeType(type, settingsData)) {
      return normalizeTimeDays(settingsData);
    }
    if (type === CustomIntervalTypeEnum.Intersection && isNotNil(settingsData?.units)) {
      return {
        units: settingsData.units.map((unit: any) =>
          unit?.unitType === "custom" && unit?.customType === "time"
            ? { ...unit, settings: normalizeTimeDays(unit.settings) }
            : unit
        ),
      };
    }
    return settingsData;
  };

  const defaultData = useMemo<CreateIntervalFormData | undefined>(
    () =>
      data?.rateInterval
        ? {
            name: data?.rateInterval.name,
            archived: data?.rateInterval.archived,
            type: data?.rateInterval.type,
            description: data?.rateInterval.description ?? "",
            settings: transformByType(data?.rateInterval.type, data?.rateInterval.settings),
          }
        : undefined,
    [data]
  );

  const form = useMuiForm<CreateIntervalFormData>({
    schema: intervalFormSchema,
    formOptions: {
      defaultValues: defaultData,
      mode: "onChange",
      reValidateMode: "onChange",
      criteriaMode: "all",
    },
  });
  const submitCreate = useFormMutationSubmit<
    CreateIntervalMutation,
    CreateIntervalMutationVariables,
    CreateIntervalFormData
  >({
    mutate: create,
    form,
    variableMapper: (dataToSend) => ({ input: dataToSend }),
  });
  const submitEdit = useFormMutationSubmit<EditIntervalMutation, EditIntervalMutationVariables, CreateIntervalFormData>(
    {
      mutate: edit,
      form,
      variableMapper: (dataToSend) => ({ id: intervalId!, input: dataToSend }),
    }
  );

  const handleSubmit = useCallback(async () => {
    if (intervalId) {
      const response = await submitEdit();
      if (response?.editRateInterval.result) {
        onClose();
        onSave();
        enqueueSnackbar(t("RateBuilder.addRateUnitSaveSuccess"), { variant: "success" });
      }
    } else {
      const response = await submitCreate();
      if (response?.createRateInterval?.result && response.createRateInterval.payload) {
        onSave(response.createRateInterval.payload);
        onClose();
        enqueueSnackbar(t("RateBuilder.addRateUnitSaveSuccess"), { variant: "success" });
      }
    }
  }, [onClose, onSave, submitCreate, submitEdit]);

  const usedInRatelists = data?.rateInterval?.ratelists ?? [];

  const selectedType = useWatch<CreateIntervalFormData, "type">({ name: "type", control: form.control });

  const isIntervalSettingsDisabled = data?.rateInterval?.modifiable === false;
  return (
    <FormContainer form={form}>
      <DialogTitle>{t("RateUnit.createCustomUnit")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {intervalId && <CreateIntervalArchivedField />}
          {isIntervalSettingsDisabled && (
            <Alert severity="warning" sx={{ display: "flex", alignItems: "center" }}>
              {t("RateUnit.unableToChangeAlreadyUsed")}
            </Alert>
          )}
          <CreateIntervalTypeField disabled={isIntervalSettingsDisabled} ratelistType={ratelistType} />
          <CreateIntervalNameField />
          <CreateIntervalDescriptionField />
          <IntervalTypeVariants disabled={isIntervalSettingsDisabled} type={selectedType as CustomIntervalTypeEnum} />
          {isNotNilOrEmpty(usedInRatelists) && (
            <CollapsibleListCard
              items={
                usedInRatelists.map((rl) => (
                  <RatelistOption
                    key={rl.id}
                    adHoc={rl.adHoc}
                    includeType
                    name={rl.name}
                    status={rl.status}
                    type={rl.type}
                    validFrom={rl.validFrom}
                    validTo={rl.validTo}
                    version={rl.version}
                  />
                )) ?? []
              }
              summaryText={t("RateUnit.useInRatelistsCount", { count: usedInRatelists?.length ?? 0 })}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} type="button">
          {t("cancel")}
        </Button>
        <Button loading={loading} onClick={handleSubmit} type="button" variant="contained">
          {t("Action.save")}
        </Button>
      </DialogActions>
    </FormContainer>
  );
};

export const IntervalTypeVariants: FC<{ type: CustomIntervalTypeEnum; disabled?: boolean; path?: string }> = ({
  disabled,
  path,
  type,
}) =>
  match<CustomIntervalTypeEnum | "" | null>(type as CustomIntervalTypeEnum)
    .with(CustomIntervalTypeEnum.Time, () => <TimeIntervalSettings disabled={disabled} path={path ?? "settings"} />)
    .with(CustomIntervalTypeEnum.Intersection, () => <IntersectionSettings disabled={disabled} path="settings" />)
    .with(CustomIntervalTypeEnum.NationalHolidays, () => <NationalHolidaysSettings disabled={disabled} path={path} />)
    .with(CustomIntervalTypeEnum.DailyOvertime, () => <DailyOvertimeSettings disabled={disabled} path={path} />)
    .with(CustomIntervalTypeEnum.WeeklyOvertime, () => <WeeklyOvertimeSettings disabled={disabled} path={path} />)
    .with(CustomIntervalTypeEnum.WorkedDuration, () => <WorkedDurationSettings disabled={disabled} path={path} />)
    .with(CustomIntervalTypeEnum.BreakPenalty, () => <BreakPenaltySettings disabled={disabled} path={path} />)
    .with("", () => null)
    .with(null, () => null)
    .exhaustive();
